import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Sparkles,
  Calendar,
  Layers,
  Boxes,
  Tag,
  Truck,
  Clock,
  DoorOpen,
  Shirt,
  Building2,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison : Charte d'Excellence ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Charte d&apos;excellence
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Livraison de cartons de déménagement {city.aCity} : notre
              Charte d&apos;Excellence
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que charger des piles de cartons encombrants dans
              votre coffre de voiture est une corvée fatigante, notre
              service de livraison s&apos;occupe de tout et
              s&apos;engage contractuellement pour vous.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Ponctualité « Zéro Retard »",
                body: "Nous fixons ensemble un créneau horaire fixe et strictement respecté. Pas de journée bloquée à attendre, notre chauffeur arrive à l'heure convenue pour vous permettre de lancer vos préparatifs sans perdre une minute.",
                Icon: Clock,
              },
              {
                title: "Intégrité « Zéro Défaut »",
                body: "Tous vos cartons et accessoires sont acheminés entièrement à plat, sanglés et protégés sous film étanche. Ils arrivent propres, secs et conservent 100 % de leur rigidité d'origine pour un pliage parfait.",
                Icon: ShieldCheck,
              },
              {
                title: "Service Porte-à-Porte Intégral",
                body: "Nous ne laissons jamais vos colis sur le trottoir. Que vous habitiez dans un pavillon ou au dernier étage d'une résidence sans ascenseur, nos livreurs déposent votre commande directement à l'intérieur de votre logement.",
                Icon: DoorOpen,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Kits Tout-en-un ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Kits prêts à l&apos;emploi
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Les Kits « Tout-en-un » : gagnez un temps précieux
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour vous éviter les calculs fastidieux et le risque de
            manquer de matériel le jour J, nos experts logistiques ont
            conçu des packs prêts à l&apos;emploi.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Adaptés à votre surface",
              body: "Du studio compact à la grande maison familiale vernolienne, nos kits regroupent la juste quantité de cartons standards, de modèles renforcés, de rouleaux d'adhésif et de film à bulles.",
              Icon: Boxes,
            },
            {
              title: "Zéro oubli",
              body: "En un seul clic, vous avez l'assurance de disposer de tout l'écosystème nécessaire pour emballer méthodiquement l'ensemble de vos pièces sans avoir à multiplier les commandes de dernière minute.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-verneuil-sur-seine-cta-kits"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir nos kits complets
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-verneuil-sur-seine-cta-accessoires"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Adhésifs &amp; protections
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Carton Penderie ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Le secret garde-robe
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Le Carton Penderie : le secret de vos garde-robes
            impeccables
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Le transfert des vêtements est souvent une source de corvée.
            Pour préserver vos habits les plus fragiles, la boîte
            classique ne suffit pas.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Structure verticale */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Shirt className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Structure verticale avec tringle
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Nos cartons penderies intègrent une tringle métallique
              amovible. Vous y suspendez directement vos costumes, robes,
              vestes et manteaux sur leurs cintres d&apos;origine, sans
              jamais les décrocher.
            </p>
            <p className="mt-3 text-[14.5px] text-[#3a4856] leading-relaxed">
              La conception verticale, à double cannelure renforcée,
              permet de superposer les pièces dans le camion sans
              écraser la garde-robe. Idéal pour les dressings
              volumineux des grandes propriétés verneuilloises où
              chaque vêtement compte.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Tringle métallique amovible
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Cintres d&apos;origine conservés
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Double cannelure renforcée
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Empilable sans écrasement
              </div>
            </div>
          </div>

          {/* Zéro pli */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Building2 className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Zéro pli, gain de temps maximal
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Vos vêtements restent parfaitement suspendus pendant le
              transport, à l&apos;abri de la poussière et du
              froissement. À l&apos;arrivée, il vous suffit de les
              replacer directement dans votre nouvelle armoire. Aucun
              repassage n&apos;est nécessaire !
            </p>
            <p className="mt-3 text-[14.5px] text-[#3a4856] leading-relaxed">
              Au-delà du gain esthétique, c&apos;est un gain de temps
              considérable lors de l&apos;emménagement {city.aCity} :
              vous pouvez consacrer vos premières journées à
              l&apos;essentiel plutôt qu&apos;à réorganiser et repasser
              une garde-robe entière.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Vêtements à l&apos;abri de la poussière
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Aucun repassage à l&apos;arrivée
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Transfert direct vers la nouvelle armoire
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Préservation des matières fragiles
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-verneuil-sur-seine-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir toute la gamme
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/livraison"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-verneuil-sur-seine-cta-livraison"
          >
            <Truck className="h-4 w-4" strokeWidth={2.6} />
            Nos modalités de livraison
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-verneuil-sur-seine-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
